import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, switchMap, of, tap, throwError } from 'rxjs';
import { AppConfig } from '@env/app-config';
import { CartStoreService } from '@prosell/feature-cart-checkout/services/cart-store.service';
import {
  MOBILE_ACCESS_TOKEN_KEY,
  MOBILE_REFRESH_TOKEN_KEY,
  MobileAuthResponse,
  MobileLoginRequest
} from '../models/mobile-auth.model';

@Injectable({ providedIn: 'root' })
export class MobileAuthService {
  private readonly http = inject(HttpClient);
  private readonly rawHttp = new HttpClient(inject(HttpBackend));
  private readonly cartStore = inject(CartStoreService);
  private readonly baseUrl = `${AppConfig.apiUrl}/api/prosell/mobile/customer-auth`;

  readonly isAuthenticated = signal(this.hasSession());

  login(username: string, password: string): Observable<MobileAuthResponse> {
    const body: MobileLoginRequest = { email: username.trim().toLowerCase(), password };
    return this.http.post<MobileAuthResponse>(`${this.baseUrl}/login`, body).pipe(
      switchMap(res => res.mfaRequired ? throwError(() => new Error('MOBILE_MFA_UNSUPPORTED')) : of(res)),
      tap(res => this.storeTokens(res))
    );
  }

  refresh(): Observable<MobileAuthResponse> {
    const refreshToken = sessionStorage.getItem(MOBILE_REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      return throwError(() => new Error('NO_MOBILE_REFRESH_TOKEN'));
    }
    return this.rawHttp.post<MobileAuthResponse>(`${this.baseUrl}/refresh`, { refreshToken }).pipe(
      tap(res => this.storeTokens(res))
    );
  }

  storeTokens(res: MobileAuthResponse): void {
    sessionStorage.setItem(MOBILE_ACCESS_TOKEN_KEY, res.accessToken);
    sessionStorage.setItem(MOBILE_REFRESH_TOKEN_KEY, res.refreshToken);
    this.isAuthenticated.set(true);
  }

  logout(): void {
    sessionStorage.removeItem(MOBILE_ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(MOBILE_REFRESH_TOKEN_KEY);
    this.isAuthenticated.set(false);
    // El carrito en memoria es de la sesión que termina (el store lo comparte con la tienda web).
    this.cartStore.limpiar();
  }

  getAccessToken(): string | null {
    return sessionStorage.getItem(MOBILE_ACCESS_TOKEN_KEY);
  }

  hasSession(): boolean {
    return !!this.getAccessToken();
  }
}
